'use client'

import React, { FC, useRef } from 'react'
import Link from '@/components/link'
import Image from 'next/image'
import dynamic from 'next/dynamic'

const FeaturedBitmapAnimation = dynamic(() => import('./featured-bitmap-animation'), { ssr: false })

const CARDS = [
	{
		slug: 'TAP-DMT-NAT',
		title: 'NAT',
		description:
			"The token that aims to answer Bitcoin's block reward halving problem. Miners get NAT tokens as a reward for mining Bitcoin blocks.",
		href: '/collection/tap-DMT-NAT',
		tag: 'Token',
		banner: '/home/nat-featured.png',
		cta: 'Trade now'
	},
	{
		slug: 'bitmap',
		title: 'Bitmap',
		description:
			'Like Ordinals, Bitmaps are also inscribed on a sat. But unlike ordinals, Bitmaps enable individuals inscribing them to own that entire block.',
		href: '/collection/bitmap',
		tag: 'Metaprotocol',
		banner: '/home/bitmap-banner.png',
		cta: 'Trade now'
	},
	{
		slug: 'bitcoin-puppets',
		title: 'Bitcoin Puppets',
		description:
			'Explore 10,001 pixel-art collectibles inscribed on Bitcoin. Discover Bitcoin Puppets, compare traits, and trade live listings on Ordinals Wallet.',
		href: '/collection/bitcoin-puppets',
		tag: 'Collection',
		banner: '/home/bitcoin-puppets.jpg',
		cta: 'View collection'
	},
	{
		slug: 'alkanes',
		title: 'Alkanes',
		description: 'True defi on Bitcoin. Discover Alkanes trading across live liquidity pools.',
		href: '/alkanes',
		tag: 'Bitcoin tokens',
		banner: '/home/alkanes.png',
		cta: 'Explore Alkanes'
	}
]

const FeaturedCard: FC<{ card: (typeof CARDS)[0]; index: number }> = ({ card, index }) => {
	return (
		<Link
			href={card.href}
			className="group flex-shrink-0 w-[85vw] md:w-[29%] md:min-w-[220px] p-1"
		>
			<div className="market-surface market-featured relative h-full flex flex-col transition-colors duration-200">
				{/* Image */}
				<div className="relative w-full aspect-[2/1] overflow-hidden rounded-t-2xl bg-chrome-muted">
					{card.slug === 'bitmap' ? <FeaturedBitmapAnimation /> : card.slug === 'TAP-DMT-NAT' ? (
						<div className="absolute inset-0 flex items-center justify-center bg-[#BDBCBB]">
							<Image src={card.banner} alt={card.title} fill priority sizes="(max-width: 767px) 85vw, (max-width: 1023px) 30vw, calc((100vw - 440px) / 3)" draggable={false} className="object-contain object-center select-none" />
						</div>
					) : <Image
						src={card.banner}
						alt={card.title}
						fill
						priority={index === 0}
						sizes="(max-width: 767px) 85vw, (max-width: 1023px) 30vw, calc((100vw - 440px) / 3)"
						draggable={false}
						onDragStart={(e) => e.preventDefault()}
						className="w-full h-full object-cover object-center select-none transform-gpu will-change-transform transition-transform duration-500 ease-out [@media(hover:hover)]:group-hover:scale-[1.025] group-focus-visible:scale-[1.025] motion-reduce:transform-none"
						style={
							{
								...(card.slug === 'bitmap' ? { objectPosition: 'center 40%' } : {}),
								WebkitUserDrag: 'none'
							} as React.CSSProperties
						}
					/>}
				</div>

				{/* Content */}
				<div className="flex flex-col gap-2 p-4 flex-1">
					<div className="flex items-center gap-2">
						<span className="text-[10px] font-medium text-violet-700 dark:text-violet-300">
							{card.tag}
						</span>
					</div>
					<div className="flex flex-col gap-1">
						<h3
							translate="no"
							className="text-base font-semibold text-foreground flex items-center gap-1.5"
						>
							{card.title}
						</h3>
						<p className="text-xs text-muted-foreground line-clamp-2">{card.description}</p>
					</div>

					<div className="mt-auto pt-1">
						<span className="inline-flex items-center justify-center rounded-md h-8 px-5 text-xs font-medium text-white bg-gradient-to-r from-purple-600 to-blue-500">
							{card.cta}
						</span>
					</div>
				</div>
			</div>
		</Link>
	)
}

const FeaturedCards: FC = () => {
	const scrollRef = useRef<HTMLDivElement>(null)
	const isDragging = useRef(false)
	const dragStartX = useRef(0)
	const scrollStartLeft = useRef(0)
	const hasDragged = useRef(false)

	const onMouseDown = (e: React.MouseEvent) => {
		if (!scrollRef.current || e.button !== 0) return
		e.preventDefault()
		isDragging.current = true
		hasDragged.current = false
		dragStartX.current = e.clientX
		scrollStartLeft.current = scrollRef.current.scrollLeft
		scrollRef.current.style.cursor = 'grabbing'
		scrollRef.current.style.userSelect = 'none'
	}

	const onMouseMove = (e: React.MouseEvent) => {
		if (!isDragging.current || !scrollRef.current) return
		const dx = e.clientX - dragStartX.current
		if (Math.abs(dx) > 3) hasDragged.current = true
		scrollRef.current.scrollLeft = scrollStartLeft.current - dx
	}

	const onMouseUp = () => {
		if (!scrollRef.current) return
		isDragging.current = false
		scrollRef.current.style.cursor = 'grab'
		scrollRef.current.style.userSelect = ''
	}

	const onClickCapture = (e: React.MouseEvent) => {
		if (hasDragged.current) {
			e.preventDefault()
			e.stopPropagation()
		}
	}

	return (
		<div className="w-full px-4 md:px-8 pt-4 md:pt-6 overflow-hidden">
			<h2 className="sr-only">Featured</h2>
			<div
				ref={scrollRef}
				role="region"
				aria-label="Featured cards — drag or scroll horizontally"
				onMouseDown={onMouseDown}
				onMouseMove={onMouseMove}
				onMouseUp={onMouseUp}
				onMouseLeave={onMouseUp}
				onDragStart={(e) => e.preventDefault()}
				onClickCapture={onClickCapture}
				className="flex gap-4 overflow-x-auto overflow-y-hidden hide-scroll-bar -mr-4 pr-4 cursor-grab touch-auto"
			>
				{CARDS.map((card, i) => (
					<FeaturedCard key={card.slug} card={card} index={i} />
				))}
			</div>
		</div>
	)
}

export default FeaturedCards
